import { Phone, Mail } from "lucide-react";
import FadeIn, { SectionHeading, Parallax } from "./FadeIn";
import TiltCard from "./motion/TiltCard";

export default function About({ onLead }) {
  return (
    <section id="about" data-testid="about-section" className="py-24 lg:py-36 bg-charcoal-800 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-center">
        <FadeIn className="lg:col-span-5">
          <Parallax speed={0.35}>
            <TiltCard max={4} className="relative max-w-md mx-auto lg:mx-0 slow-float">
              {/* champagne-gold lighting */}
              <div className="absolute -inset-14 bg-gold/[0.1] blur-3xl rounded-full pointer-events-none" aria-hidden="true" />
              {/* subtle architectural backdrop lines */}
              <div className="absolute -inset-10 pointer-events-none" aria-hidden="true" style={{ transform: "translateZ(-46px)" }}>
                <div className="absolute left-6 top-0 w-px h-3/4 bg-gold/15" />
                <div className="absolute right-10 bottom-0 w-px h-1/2 bg-gold/10" />
                <div className="absolute left-0 top-10 h-px w-2/3 bg-gold/10" />
                <div className="absolute right-0 top-1/3 h-px w-1/3 bg-gold/15" />
                <div className="absolute right-6 top-6 w-24 h-24 border border-gold/15 rounded-full" />
              </div>
              {/* offset gold frame behind */}
              <div
                className="absolute -inset-4 border border-gold/25 rounded-md pointer-events-none"
                style={{ transform: "translate(20px, 20px) translateZ(-30px)" }}
              />
              <img
                src="/akashdeep-ghai.jpg"
                alt="Akashdeep Ghai, Mortgage Agent"
                loading="lazy"
                className="relative w-full h-[520px] object-cover object-top rounded-md border border-gold/30 shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
                style={{ transform: "translateZ(24px)" }}
              />
              {/* floating glass frame above the portrait */}
              <div
                className="absolute -inset-2.5 rounded-md border border-gold/30 bg-white/[0.03] pointer-events-none shadow-[inset_0_1px_0_rgba(243,229,171,0.15)]"
                style={{ transform: "translate(-12px, -12px) translateZ(48px)" }}
                aria-hidden="true"
              />
              {/* floating glass nameplate */}
              <div
                className="absolute left-5 bottom-5 glass-panel rounded-sm px-5 py-3 pointer-events-none"
                style={{ transform: "translateZ(56px)" }}
              >
                <p className="font-serif text-lg text-ivory tracking-[0.12em]">AKASHDEEP GHAI</p>
                <p className="text-[9px] tracking-[0.3em] text-gold mt-1">MORTGAGE AGENT</p>
              </div>
            </TiltCard>
          </Parallax>
        </FadeIn>

        <div className="lg:col-span-7">
          <SectionHeading label="Meet Your Advisor" title="Your Mortgage. Your Strategy." />
          <FadeIn delay={0.15}>
            <p className="mt-8 font-serif text-3xl tracking-[0.08em] text-champagne" data-testid="about-agent-name">AKASHDEEP GHAI</p>
            <p className="mt-2 text-[11px] uppercase tracking-[0.24em] text-gold/80">Mortgage Agent · Canada</p>

            <div className="mt-8 glass-panel rounded-md p-7 space-y-5" data-testid="about-bio">
              <p className="text-ivory/60 font-light leading-relaxed text-[15px]">
                I am a dedicated and experienced mortgage professional committed to helping Canadians secure financing solutions tailored to their unique goals and circumstances. With extensive experience navigating complex and challenging situations, I provide thoughtful guidance, practical solutions, and personalized service throughout the mortgage process.
              </p>
              <p className="text-ivory/60 font-light leading-relaxed text-[15px]">
                Whether you are purchasing your first home, refinancing, consolidating debt, or exploring improved mortgage options, I take the time to understand your needs and identify solutions aligned with your financial objectives.
              </p>
              <p className="text-ivory/60 font-light leading-relaxed text-[15px]">
                My approach is grounded in honest advice, personalized service, and clear communication from start to finish. I believe the mortgage process should be straightforward and manageable — my goal is to make each step as smooth and transparent as possible, empowering you to make informed financial decisions with confidence.
              </p>
              <p className="text-ivory/60 font-light leading-relaxed text-[15px]">
                Every client's circumstances are unique, and I work diligently to develop solutions for a broad range of credit and financial situations. Your goals remain my priority, and I am here to provide professional guidance and support at every stage.
              </p>
            </div>

            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a
                href="tel:+18257362193"
                data-testid="about-call-btn"
                className="flex-1 inline-flex items-center justify-center gap-2.5 px-6 py-4 text-xs tracking-[0.2em] font-semibold text-charcoal bg-gold hover:bg-champagne transition-colors duration-300 rounded-sm"
              >
                <Phone size={14} strokeWidth={1.75} /> CALL AKASHDEEP
              </a>
              <a
                href="mailto:akkasghai@gmail.com"
                data-testid="about-email-btn"
                className="flex-1 inline-flex items-center justify-center gap-2.5 px-6 py-4 text-xs tracking-[0.2em] font-medium text-ivory border border-white/20 hover:border-gold/60 hover:text-gold transition-colors duration-300 rounded-sm"
              >
                <Mail size={14} strokeWidth={1.75} /> EMAIL AKASHDEEP
              </a>
            </div>
            <button
              data-testid="about-book-consultation-btn"
              onClick={() => onLead("consultation")}
              className="mt-4 w-full px-6 py-4 text-xs tracking-[0.2em] font-medium text-ivory border border-white/20 hover:border-gold/60 hover:text-gold transition-colors duration-300 rounded-sm"
            >
              BOOK A CONSULTATION
            </button>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}
